import React, { useCallback } from 'react';
import { object, func, bool, string } from 'prop-types';
import { useIntl } from 'react-intl';

import BookmarkButton from '@alltrails/shared/components/BookmarkButton';
import { wrapEventHandler } from 'utils/handlers';
import useToggle from '../../../hooks/useToggle';
import { monthDayYear } from '../../../utils/time_helpers';
import { buildMapMenuItems, buildTrackMenuItems } from '../../../utils/action_header_helper';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { getTrackProfilePhotoUrl } from '../../../utils/track_helpers';
import { getImgDefaultStyle } from '../../cards/CardImage/CardImage';
import OriginalLanguageButton from '../OriginalLanguageButton';
import HeaderButtons from '../HeaderButtons';
import StarRating from '../StarRating';
import * as styles from './styles/styles.module.scss';
import CustomProvider from 'components/CustomProvider';
import useLocalizedText from 'hooks/useLocalizedText';

const ActionHeader = ({
  data,
  user,
  context,
  isFavorite,
  handleFavoriteClick,
  bannerPhotoId,
  handleDirectionsClick,
  openDownloadModal,
  handleShareClick,
  handleDeleteClick,
  saveMapAsClone,
  type,
  editingEndpoint,
  handleEditEndpointClick,
  handleCancelEditEndpointClick,
  handleEditClick
}) => {
  const intl = useIntl();
  let refreshMap;
  let updateHidden;

  const [refreshed, toggleRefreshed] = useToggle(false);
  const [hidden, toggleHidden] = useToggle(data?.hidden);
  const id = type === 'map' ? data.id : data.ID;
  const descriptionSource = data?.description_source || data?.description;
  const {
    isTranslatable,
    toggleText,
    toggledLocaleText,
    localizedText,
  } = useLocalizedText(descriptionSource, data?.description_lang, 'activities', 'description', id);

  if (context.adminUser) {
    refreshMap = () => {
      ServerCommunicationUtil.getApiEndpoint(
        `/api/alltrails/maps/${id}/refresh_map_cache`,
        {},
        () => toggleRefreshed(true),
        err => console.log(err),
        () => {}
      );
    };

    updateHidden = () => {
      ServerCommunicationUtil.postApiEndpoint(
        `/api/alltrails/maps/${data.id}/metadata`,
        { hidden: !hidden },
        () => toggleHidden(!hidden),
        () => {},
        () => {}
      );
    };
  }

  const favoriteClick = () => {
    handleFavoriteClick(data.presentationType, id);
  };

  const addAsNewMap = () => {
    window.open(LanguageSupportUtil.wrapUrlSafe(`/trail/new?recording=${data.id}`, context.languageRegionCode), '_blank');
  };

  const getBannerImgStyle = useCallback(() => {
    const mapPhotoUrl = bannerPhotoId
      ? `/api/alltrails/photos/${bannerPhotoId}/image?size=large&key=${ServerCommunicationUtil.apiKey}`
      : getTrackProfilePhotoUrl(data);
    const backgroundImage = `linear-gradient(rgba(0, 0, 0, 0.0), rgba(0, 0, 0, 0.7)), url(${mapPhotoUrl})`;
    return getImgDefaultStyle(data.name, backgroundImage);
  }, [data, bannerPhotoId]);

  const isMapOwner = context.currentUser?.id === data.user?.id;

  const getMenuItems = () => {
    if (type === 'map') {
      return buildMapMenuItems(
        context,
        data,
        openDownloadModal,
        refreshed,
        refreshMap,
        hidden,
        updateHidden,
        handleDeleteClick,
        addAsNewMap,
        isMapOwner,
        saveMapAsClone,
        intl
      );
    }
    if (type === 'track') {
      return buildTrackMenuItems(
        context,
        data,
        openDownloadModal,
        editingEndpoint,
        handleEditEndpointClick,
        handleCancelEditEndpointClick,
        handleEditClick,
        refreshed,
        refreshMap,
        handleDeleteClick,
        saveMapAsClone,
        intl
      );
    }
    return null;
  };

  const activityToUpperCase = name => name.charAt(0).toUpperCase() + name.slice(1);

  return (
    <CustomProvider>
      <div className={styles.container}>
        <div className={styles.banner} style={getBannerImgStyle()}>
          <BookmarkButton className={styles.bookmarkButton} isSelected={isFavorite} onClick={wrapEventHandler(favoriteClick)} />
        </div>
        <HeaderButtons
          context={context}
          user={user}
          handleDirectionsClick={handleDirectionsClick}
          handleShareClick={handleShareClick}
          menuItems={getMenuItems()}
        />
        <div className={styles.metadata}>
          <div className={styles.dateAndActivity}>
            {monthDayYear(data.created_at, context.languageRegionCode)}{' '}
            {data.activity?.name ? `${'\u2022'} ${activityToUpperCase(data?.activity.name)}` : ''}
          </div>
          {type === 'map' ? (
            <div className={styles.nameAndDescription}>
              <div className={styles.name}>{data.name}</div>
              <div className={styles.description}>{localizedText}</div>
              {isTranslatable && (
                <OriginalLanguageButton
                  active={toggledLocaleText}
                  handleClick={toggleText}
                  eventSource="Map Description"
                />
              )}
            </div>
          ) : (
            <div className={styles.nameAndStars}>
              <div className={styles.trackName}>{data.name}</div>
              <span className={styles.starsBlock} itemProp="aggregateRating" itemScope itemType="http://schema.org/AggregateRating">
                <StarRating config={data.rating} />
                <meta itemProp="ratingValue" content={data.rating == null ? '0' : data.rating} />
                <meta itemProp="worstRating" content="0" />
                <meta itemProp="bestRating" content="5" />
                <meta itemProp="reviewCount" content="1" />
              </span>
              <div className={styles.description}>{localizedText}</div>
              {isTranslatable && (
                <OriginalLanguageButton
                  active={toggledLocaleText}
                  handleClick={toggleText}
                  eventSource="Map Description"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </CustomProvider>
  );
};

ActionHeader.propTypes = {
  data: object.isRequired,
  context: object.isRequired,
  user: object,
  isFavorite: bool,
  handleFavoriteClick: func,
  bannerPhotoId: string,
  handleDirectionsClick: func,
  openDownloadModal: func,
  handleShareClick: func,
  saveMapAsClone: func,
  type: string,
  editingEndpoint: bool,
  handleEditEndpointClick: func,
  handleCancelEditEndpointClick: func,
  handleEditClick: func,
  handleDeleteClick: func
};

ActionHeader.defaultProps = {
  user: {},
  isFavorite: false,
  handleFavoriteClick: () => {},
  bannerPhotoId: null,
  handleDirectionsClick: () => {},
  openDownloadModal: () => {},
  handleShareClick: () => {},
  saveMapAsClone: () => {},
  type: '',
  editingEndpoint: false,
  handleEditEndpointClick: () => {},
  handleCancelEditEndpointClick: () => {},
  handleEditClick: () => {},
  handleDeleteClick: () => {}
};

export default ActionHeader;
